/**
 * The Super Admin's landing page: the platform at a glance.
 *
 * Nothing here is scoped to a store or a merchant, and it shares no widgets
 * with the merchant `DashboardPage`. Every figure comes from `/v1/admin/overview`
 * or `/health`; where there is nothing to show, it says so.
 */

import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, health } from '../../lib/api.js'
import { ago, humanise, money, moneyExact, percent } from '../../lib/format.js'
import { Badge, Card, Empty, KeyValue, PageHead, RowsSkeleton, Stat, StatsSkeleton } from '../../components/ui.js'
import {
  BuildingIcon,
  DownloadIcon,
  LedgerIcon,
  RupeeIcon,
  ShieldIcon,
  StoreIcon,
  TrendUpIcon,
  UsersIcon,
  WalletIcon,
} from '../../components/icons.js'

export interface PlatformOverview {
  windowDays: number
  merchants: number
  activeMerchants: number
  shops: number
  users: number
  newUsers: number
  transactions: { count: number; credited: string; debited: string }
  gmv: string
  orders: number
  commission: string
  takeRate: number | null
  walletFloat: string
  lowBalance: Array<{ id: string; name: string; balance: string; lowSince: string | null }>
  activity: Array<{ id: string; type: string; store: string | null; at: string }>
}

export function SuperAdminDashboard(): React.ReactNode {
  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api<PlatformOverview>('/v1/admin/overview'),
  })

  return (
    <>
      <PageHead
        title="Super Admin dashboard"
        subtitle={data ? `The whole platform, over the last ${data.windowDays} days.` : 'Every merchant, every store, every rupee.'}
        actions={
          <button type="button" className="btn" onClick={() => window.print()}>
            <DownloadIcon size={16} />
            Export
          </button>
        }
      />

      {error && (
        <Card>
          <Empty error title="Could not load the platform overview">
            {(error as Error).message}
          </Empty>
        </Card>
      )}

      {isPending && <StatsSkeleton count={8} />}

      {data && (
        <>
          <section className="grid grid--4">
            <Stat
              label="Registered users"
              value={data.users.toLocaleString('en-IN')}
              icon={<UsersIcon size={20} />}
              tone="var(--brand)"
              footnote={
                <span className="faint">
                  {data.newUsers.toLocaleString('en-IN')} new in {data.windowDays}d
                </span>
              }
            />
            <Stat
              label="Merchants"
              value={data.merchants.toLocaleString('en-IN')}
              icon={<BuildingIcon size={20} />}
              tone="var(--info)"
              footnote={<span className="faint">{data.activeMerchants} active</span>}
            />
            <Stat label="Stores" value={data.shops.toLocaleString('en-IN')} icon={<StoreIcon size={20} />} tone="var(--warn)" />
            <Stat
              label={`Revenue (${data.windowDays}d)`}
              value={money(data.commission)}
              icon={<TrendUpIcon size={20} />}
              tone="var(--ok)"
              footnote={<span className="faint">commission earned</span>}
            />
            <Stat label={`GMV (${data.windowDays}d)`} value={money(data.gmv)} icon={<RupeeIcon size={20} />} tone="var(--brand)" />
            <Stat
              label="Take rate"
              value={data.takeRate === null ? '—' : percent(data.takeRate, 2)}
              icon={<LedgerIcon size={20} />}
              tone="var(--accent)"
              footnote={<span className="faint">commission over GMV</span>}
            />
            <Stat
              label={`Transactions (${data.windowDays}d)`}
              value={data.transactions.count.toLocaleString('en-IN')}
              icon={<LedgerIcon size={20} />}
              tone="var(--info)"
              footnote={<span className="faint">settled wallet ledger entries</span>}
            />
            <Stat
              label="Wallet float held"
              value={money(data.walletFloat)}
              icon={<WalletIcon size={20} />}
              tone="var(--bad)"
              footnote={<span className="faint">merchant money on platform</span>}
            />
          </section>

          <div className="grid grid--2">
            <Card
              title="Wallets below the floor"
              flush
              action={<Badge tone={data.lowBalance.length > 0 ? 'bad' : 'ok'}>{data.lowBalance.length}</Badge>}
            >
              {data.lowBalance.length === 0 ? (
                <Empty icon={<WalletIcon size={22} />} title="Every merchant is above the floor" />
              ) : (
                <ul className="list">
                  {data.lowBalance.map((merchant) => (
                    <li key={merchant.id}>
                      <span
                        className="feature__icon"
                        style={{ ['--tone' as string]: Number(merchant.balance) < 0 ? 'var(--bad)' : 'var(--warn)' }}
                      >
                        <WalletIcon size={16} />
                      </span>
                      <span className="list__text">
                        <strong>{merchant.name}</strong>
                        <small>{merchant.lowSince ? `Low since ${ago(merchant.lowSince)}` : 'Just dropped below'}</small>
                      </span>
                      <span className="list__value" style={{ color: Number(merchant.balance) < 0 ? 'var(--bad)' : undefined }}>
                        {moneyExact(merchant.balance)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="Volume" subtitle={`Settled in the last ${data.windowDays} days`}>
              <KeyValue
                rows={[
                  { label: 'Settled orders', value: data.orders.toLocaleString('en-IN') },
                  { label: 'GMV', value: money(data.gmv) },
                  { label: 'Wallet credits', value: money(data.transactions.credited) },
                  { label: 'Wallet debits', value: money(data.transactions.debited) },
                  { label: 'Commission earned', value: money(data.commission), kind: 'total' },
                ]}
              />
              <Link to="/admin/ledger" className="btn btn--block" style={{ marginTop: 16 }}>
                <LedgerIcon size={16} />
                Open the ledger
              </Link>
            </Card>

            <Card title="Recent system activity" flush>
              {data.activity.length === 0 ? (
                <Empty icon={<ShieldIcon size={22} />} title="No activity recorded yet" />
              ) : (
                <ul className="list">
                  {data.activity.map((event) => (
                    <li key={event.id}>
                      <span className="list__text">
                        <strong>{humanise(event.type)}</strong>
                        <small>{event.store ?? 'Platform'}</small>
                      </span>
                      <span className="list__value faint">{ago(event.at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <SystemStatusCard />
          </div>
        </>
      )}
    </>
  )
}

function SystemStatusCard(): React.ReactNode {
  // `health()`, not `api()`: see AdminHealthPage — a degraded answer is still
  // the answer, and this is when it matters most.
  const { data, isPending } = useQuery({
    queryKey: ['health'],
    queryFn: ({ signal }) => health(signal),
    staleTime: 0,
  })

  const service = (state: string): React.ReactNode => <Badge tone={state === 'up' ? 'ok' : 'bad'}>{humanise(state)}</Badge>

  return (
    <Card title="System status" subtitle="Live from the API's /health endpoint">
      {isPending || !data ? (
        <RowsSkeleton rows={4} />
      ) : (
        <KeyValue
          rows={[
            { label: 'Postgres', value: service(data.postgres) },
            { label: 'Redis', value: service(data.redis) },
            { label: 'Realtime connections', value: data.realtime.connections.toLocaleString('en-IN') },
            { label: 'API version', value: data.version ?? 'unknown' },
          ]}
        />
      )}
      <Link to="/admin/health" className="btn btn--block" style={{ marginTop: 16 }}>
        <ShieldIcon size={16} />
        Health &amp; audit
      </Link>
    </Card>
  )
}
